import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  IconButton,
  Grid,
  Box,
  Typography,
  Drawer,
  Paper,
  Stack,
  Toolbar,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ArrowBack } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ServiceField from './ServiceField';

export default function ServicesDrawer({
  open,
  handleClose,
  initialValues,
  setValue,
  error,
  setError,
}) {
  const projectServicesRaw = useSelector((state) => state.projectServices);
  const projectServices = useMemo(
    () => projectServicesRaw || [],
    [projectServicesRaw]
  );

  // Helper to get default target port
  const getDefaultTargetPort = (serviceType) => {
    const normalizedType = serviceType?.toLowerCase();
    if (normalizedType === 'https') return '80';
    if (normalizedType === 'http2') return '443';
    return undefined;
  };

  // Initialize services with default ports for HTTPS/HTTP2
  const initializeServices = useCallback((services, servicesList) => {
    const safe_services = Array.isArray(services) ? services : [];
    return safe_services.map((service) => {
      const serviceObj = servicesList.find(
        (s) => (s.id || s.service_id) === service.id
      );
      const serviceType = serviceObj?.type;
      
      // If service is HTTPS/HTTP2 and doesn't have target_port, set default
      if (serviceType && !service.target_port) {
        const defaultPort = getDefaultTargetPort(serviceType);
        if (defaultPort) {
          return { ...service, target_port: defaultPort };
        }
      }
      
      return { ...service };
    });
  }, []);

  const safe_initial_values = Array.isArray(initialValues) ? initialValues : [];

  const [selectedServices, setSelectedServices] = useState(
    initializeServices(safe_initial_values, projectServices)
  );

  const [service_errors, setServiceErrors] = useState(
    new Array(safe_initial_values.length).fill(false)
  );

  const selectedServicesRef = useRef(
    initializeServices(safe_initial_values, projectServices)
  );

  useEffect(() => {
    selectedServicesRef.current = selectedServices;
  }, [selectedServices]);

  useEffect(() => {
    const safe = Array.isArray(initialValues) ? initialValues : [];
    const new_services = initializeServices(safe, projectServices);
    selectedServicesRef.current = new_services;
    setSelectedServices(new_services);
    setServiceErrors(new Array(safe.length).fill(false));
  }, [initialValues, projectServices, initializeServices]);

  const serviceErrorCheck = useCallback((services) => {
    const errors = services.map((service, index) => {
      if (!service.id) {
        return 'Service is required';
      }

      const serviceObj = projectServices.find(
        (s) => (s.id || s.service_id) === service.id
      );
      if (!serviceObj) {
        return 'Service not found';
      }

      // Check for duplicate HTTPS/HTTP2 services
      const serviceType = serviceObj.type;
      if (serviceType === 'HTTPS' || serviceType === 'HTTP2' || serviceType === 'https' || serviceType === 'http2') {
        const normalizedType = serviceType.toUpperCase();
        const sameTypeCount = services.filter((s) => {
          if (!s.id) return false;
          const sObj = projectServices.find(
            (svc) => (svc.id || svc.service_id) === s.id
          );
          const sType = sObj?.type?.toUpperCase();
          return sType === normalizedType;
        }).length;

        if (sameTypeCount > 1) {
          return `Cannot select more than one ${normalizedType} service`;
        }
      }

      // Check target_port for tcp/udp
      const normalizedServiceType = serviceType?.toLowerCase();
      if ((normalizedServiceType === 'tcp' || normalizedServiceType === 'udp') && !service.target_port) {
        return 'Target port is required for TCP/UDP services';
      }

      return false;
    });

    return errors;
  }, [projectServices]);

  const calculateError = useCallback(
    (service_errors) => {
      const errors_set = new Set([...service_errors]);
      if (
        errors_set.size > 1 ||
        ([...errors_set][0] && [...errors_set][0] !== false)
      ) {
        setError(true);
      } else {
        setError(false);
      }
    },
    [setError]
  );

  const setServiceError = useCallback(
    (i, error) => {
      setServiceErrors((prevErrors) => {
        const new_errors = [...prevErrors];
        new_errors[i] = error;

        // Recalculate errors using current selectedServices from ref
        const validationErrors = serviceErrorCheck(selectedServicesRef.current);
        const combinedErrors = new_errors.map((err, idx) => {
          // If there's an explicit error (string), keep it, otherwise use validation check
          return err && err !== false ? err : validationErrors[idx];
        });
        calculateError(combinedErrors);
        return combinedErrors;
      });
    },
    [calculateError, serviceErrorCheck]
  );

  const addService = useCallback(() => {
    const new_services = [
      ...selectedServices,
      { id: '', target_port: undefined },
    ];
    selectedServicesRef.current = new_services;
    setSelectedServices(new_services);
    const new_errors = [...service_errors, 'Service is required'];
    setServiceErrors(new_errors);
    calculateError(new_errors);
  }, [
    selectedServices,
    service_errors,
    setSelectedServices,
    calculateError,
    setServiceErrors,
  ]);

  const removeService = useCallback(
    (i) => {
      const new_services = [...selectedServices];
      new_services.splice(i, 1);
      selectedServicesRef.current = new_services;

      setSelectedServices(new_services);
      const new_errors = [...service_errors];
      new_errors.splice(i, 1);

      // Recheck for errors after removal
      const validationErrors = serviceErrorCheck(new_services);
      const combinedErrors = new_errors.map((err, idx) => {
        return err && err !== false ? err : validationErrors[idx];
      });

      setServiceErrors(combinedErrors);
      calculateError(combinedErrors);
    },
    [
      selectedServices,
      service_errors,
      setSelectedServices,
      setServiceErrors,
      calculateError,
      serviceErrorCheck,
    ]
  );

  const setServiceValue = useCallback(
    (i, values) => {
      setSelectedServices((prevServices) => {
        const new_services = [...prevServices];
        new_services[i] = values;
        selectedServicesRef.current = new_services;

        // Recheck for errors after value change
        setServiceErrors((prevErrors) => {
          const validationErrors = serviceErrorCheck(new_services);
          const currentErrors = [...prevErrors];
          // Always use the validation result - it will be false if valid
          currentErrors[i] = validationErrors[i];
          calculateError(currentErrors);
          return currentErrors;
        });

        return new_services;
      });
    },
    [serviceErrorCheck, calculateError]
  );

  const submit = useCallback(() => {
    // Filter out services without IDs and clean up the structure
    const cleanedServices = selectedServices
      .filter((service) => service.id)
      .map((service) => {
        const serviceObj = projectServices.find(
          (s) => (s.id || s.service_id) === service.id
        );
        const serviceType = serviceObj?.type?.toLowerCase() || '';
        const result = { id: service.id };
        
        // Set default ports for HTTPS/HTTP2, include target_port for TCP/UDP if set
        if (serviceType === 'https') {
          result.target_port = '80';
        } else if (serviceType === 'http2') {
          result.target_port = '443';
        } else if (service.target_port && (serviceType === 'tcp' || serviceType === 'udp')) {
          result.target_port = service.target_port;
        }
        
        return result;
      });
    
    setValue(cleanedServices);
    handleClose();
  }, [selectedServices, setValue, handleClose, projectServices]);

  return (
    <Drawer
      sx={{ height: '100%' }}
      anchor='right'
      open={open}
      onClose={handleClose}
    >
      <Grid
        container
        spacing={2}
        direction='row'
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          margin: '0px',
          padding: '0px 0px 0px 0px !important',
          '.MuiGrid-root': {
            padding: '0px 0px 0px 0px',
          },
          maxWidth: '550px',
        }}
      >
        <Grid
          container
          justifyContent='flex-start'
          sx={{
            maxHeight: '75px',
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
            }}
            elevation={1}
          >
            <Toolbar>
              <Stack spacing={2} direction='row' alignItems='center'>
                <IconButton onClick={() => handleClose()}>
                  <ArrowBack />
                </IconButton>
                <Typography variant='h5'>Manage Regional Services</Typography>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
        <Grid
          size={{ xs: 12, md: 12, lg: 12 }}
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
          }}
        >
          <Box sx={{ padding: '16px' }}>
            <Stack spacing={2} direction='column' alignItems='center'>
              {selectedServices.map((value, index) => {
                return (
                  <ServiceField
                    key={`service-${index}`}
                    index={index}
                    value={value}
                    error={service_errors[index]}
                    setError={setServiceError}
                    setValue={setServiceValue}
                    remove={removeService}
                    projectServices={projectServices}
                  />
                );
              })}
              <Button
                variant='outlined'
                onClick={() => addService()}
                color='brand'
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <AddCircleOutlineIcon />
                  <Typography variant='button' display='block' gutterBottom>
                    Add Service
                  </Typography>
                </Stack>
              </Button>
            </Stack>
          </Box>
        </Grid>
        <Grid
          container
          sx={{
            maxHeight: '50px',
            position: 'absolute',
            bottom: '0px',
            width: '100%',
          }}
        >
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='contained'
                  onClick={submit}
                  disabled={error}
                  color='brand'
                >
                  Save
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    const safe = Array.isArray(initialValues) ? initialValues : [];
                    setSelectedServices(safe.map((service) => ({ ...service })));
                    setServiceErrors(new Array(safe.length).fill(false));
                    setError(false);
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
      </Grid>
    </Drawer>
  );
}

